// src/views/menage/MonTravail.jsx — « MON TRAVAIL » de l'agent de ménage,
// reconstruit d'après la maquette NEST v677 (espace Ménage › Mon travail,
// 1280 et 390 px), lot G2 du 30/10.
//
// CE QUE L'ÉCRAN MONTRE, dans l'ordre de la maquette :
//   1. le bandeau « Ce qu'on dit de vos ménages, et les reprises. » et ses
//      quatre tuiles (ménages faits · propreté · avis sous 5 sur 5 · repassé
//      derrière vous) ;
//   2. « ‹ Août 2026 › » et la liste « Tous les logements » ;
//   3. « Les avis à regarder » puis « Les autres avis », repliés ;
//   4. « Quand on est repassé derrière vous · <mois> » : aubergine « Repris
//      sur place, pas par vous », vert « Vous l'avez repris vous-même », et
//      « Voir la photo du constat ».
//
// D'OÙ VIENNENT LES DONNÉES — seulement les portes du serveur :
//   · les avis : `menage_avis` (périmètre résolu par le serveur) ;
//   · les reprises : `controle_agent_historique` (jamais le contrôleur) ;
//   · les ménages du mois : `ecran_agent_mois_lire` ;
//   · le nom des logements : `menage_log_info` (guichet du ménage), jamais la
//     configuration complète. Un logement que le guichet ne connaît pas
//     s'appelle « un logement », jamais par son identifiant.
//
// RELECTURE #1748 (30/10) : une lecture ratée côté serveur arrive en erreur
// (plus jamais une liste vide « réussie ») et s'affiche comme telle ; les
// tuiles ouvrent leur liste ; « repassé derrière vous » compte toutes les
// reprises ; les mois ne remontent pas au-delà des 12 lus par le serveur.
//
// LA TUILE « MÉNAGES FAITS » : la porte `ecran_agent_mois_lire` compte ses
// ménages faits du mois ; « validés du premier coup » = faits moins reprises.
// Une lecture ratée affiche « — » (je ne sais pas), jamais 0. La tuile suit le
// mois et le logement choisis.
//
// Couleurs (règle de la maquette) : vert = bon, aubergine = à regarder,
// gris = chiffre à lire. Échelle des notes unique (journal du 28/10) :
// 5 = vert, 4,8 à moins de 5 = gris, sous 4,8 = aubergine.

import { useState, useEffect, useMemo } from 'react'
import { fetchMenageAvis } from '../../lib/menage-avis-api'
import { fetchHistoriqueControles } from '../../lib/controle-agent-api'
import { fetchLogInfos } from '../../lib/menage-log-info-cache'
import { fetchMoisAgent } from '../../lib/agent-journee-api'
import { SANS_NOM } from '../../lib/agent-journee-regles'
import useEchapPourFermer from '../../lib/useEchapPourFermer'
import { Brush, Star, RotateCcw, ChevronDown } from 'lucide-react'
import { NEST, POLICE } from './palette-nest'

export { NEST }

function dateCourteFR(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}
function moisCourantParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date()).slice(0, 7)
}
function libelleMoisFR(month) {
  const t = Date.parse(`${month}-01T12:00:00Z`)
  if (Number.isNaN(t)) return month
  const s = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' }).format(t)
  return s.charAt(0).toUpperCase() + s.slice(1)
}
// Dans une phrase, le mois seul (« en août »), jamais l'année.
function libelleMoisSeul(month) {
  const t = Date.parse(`${month}-01T12:00:00Z`)
  if (Number.isNaN(t)) return month
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'Europe/Paris' }).format(t)
}
function moisVoisin(month, pas) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + pas, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
const moisDe = (iso) => String(iso || '').slice(0, 7)
// Le serveur ne lit que 12 mois de reprises : on ne remonte pas au-delà (sinon
// un mois jamais lu s'afficherait « 0 », en vert).
export const MOIS_LUS = 12
export function moisLePlusAncien(courant) { return moisVoisin(courant, -(MOIS_LUS - 1)) }

// Une note « à la française » : 4,8 ; 5 ; « — » quand on ne sait pas.
export function noteFR(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—'
  const v = Math.round(Number(n) * 10) / 10
  return String(v).replace('.', ',')
}
export function noteMenage(a) {
  const v = Number(a?.category_ratings?.cleanliness)
  return a?.category_ratings?.cleanliness != null && Number.isFinite(v) ? v : null
}
export function noteGenerale(a) {
  if (a?.rating == null) return null
  const v = Number(a.rating)
  return Number.isFinite(v) ? v : null
}
// La note générale ramenée sur 5 (Booking note sur 10).
export function noteSur5(a) {
  const n = noteGenerale(a)
  if (n == null) return null
  return String(a?.source || '').toLowerCase().includes('booking') || n > 5 ? n / 2 : n
}
// L'échelle unique sur 5 : 'bon' (vert), 'correct' (gris), 'regarder' (aubergine).
export function palierNote(n) {
  if (n == null || !Number.isFinite(Number(n))) return 'inconnu'
  if (Number(n) >= 5) return 'bon'
  if (Number(n) >= 4.8) return 'correct'
  return 'regarder'
}
// Les points d'un constat, en une phrase : « Traces sur le miroir · poussière ».
export function phraseConstat(points) {
  return (Array.isArray(points) ? points : []).filter(Boolean)
    .map((pt) => String(pt?.texte || '').trim() || humaniser(pt?.categorie))
    .filter(Boolean).join(' · ')
}
function humaniser(id) {
  const s = String(id || '').replace(/[_-]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
}

const TONS = {
  bon: { fond: NEST.vertPale, chiffre: NEST.vertFonce, texte: NEST.vertTexte },
  regarder: { fond: NEST.aubPale, chiffre: NEST.aubTexte, texte: NEST.aub },
  correct: { fond: NEST.grisPale, chiffre: NEST.encre, texte: NEST.gris },
  inconnu: { fond: NEST.grisPale, chiffre: NEST.encre, texte: NEST.gris },
}

function Tuile({ cle, ton, chiffre, texte, Icone, plein, titre, ouvert, onOuvrir }) {
  const t = TONS[ton] || TONS.inconnu
  const Bloc = onOuvrir ? 'button' : 'div'
  return (
    <Bloc data-testid={`travail-bloc-${cle}`} data-ton={ton}
      {...(onOuvrir ? { type: 'button', onClick: onOuvrir, 'aria-expanded': !!ouvert } : {})}
      style={{ background: t.fond, borderRadius: 12, padding: '9px 11px', minHeight: 44, textAlign: 'left',
        border: `1.5px solid ${ouvert ? t.chiffre : 'transparent'}`, fontFamily: POLICE,
        cursor: onOuvrir ? 'pointer' : 'default', display: 'block', width: '100%' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {Icone && (
          <span title={titre} aria-label={titre} role="img" style={{ display: 'inline-flex', flex: 1 }}>
            <Icone size={17} strokeWidth={2} color={t.chiffre} fill={plein ? t.chiffre : 'none'} />
          </span>
        )}
        {onOuvrir && (
          <ChevronDown size={17} strokeWidth={2} color={t.chiffre} aria-hidden="true"
            style={{ transform: ouvert ? 'rotate(180deg)' : 'none' }} />
        )}
      </span>
      <b style={{ display: 'block', fontSize: 22, fontWeight: 600, color: t.chiffre, lineHeight: 1.2 }}>{chiffre}</b>
      <span style={{ display: 'block', fontSize: 11, color: t.texte, lineHeight: 1.35 }}>{texte}</span>
    </Bloc>
  )
}

// Une ligne du panneau ouvert sous les tuiles (avis ou reprise).
function LigneDetail({ note, titre, sousTitre, etiquette, tonEtiquette }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: `1px solid ${NEST.bord}` }}>
      {note != null && (
        <span style={{ minWidth: 34, fontSize: 15, fontWeight: 700, color: note >= 5 ? NEST.vertFonce : NEST.aubTexte }}>
          {noteFR(note)}<em style={{ fontSize: 10, fontStyle: 'normal', color: NEST.gris }}>/5</em>
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <b style={{ display: 'block', fontSize: 12.5 }}>{titre}</b>
        {sousTitre && <span style={{ display: 'block', fontSize: 11, color: NEST.grisTexte }}>{sousTitre}</span>}
      </span>
      {etiquette && (
        <span style={{ borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
          background: tonEtiquette === 'bon' ? NEST.vertPale : NEST.aubPale,
          color: tonEtiquette === 'bon' ? NEST.vertFonce : NEST.aubTexte }}>{etiquette}</span>
      )}
    </div>
  )
}

function Etoiles({ n, couleur = NEST.encre }) {
  if (n == null) return null
  const pleines = Math.max(0, Math.min(5, Math.round(n)))
  return (
    <span aria-label={`note ${noteFR(n)} sur 5`} title={`note ${noteFR(n)} sur 5`}
      style={{ color: couleur, fontSize: 13, letterSpacing: 1, whiteSpace: 'nowrap' }}>
      {'★'.repeat(pleines)}
    </span>
  )
}

const intertitre = { fontSize: 11.5, fontWeight: 700, color: NEST.vertFonce, letterSpacing: '.04em',
  textTransform: 'uppercase', margin: '18px 2px 8px' }
const bouton = { minHeight: 44, padding: '0 15px', borderRadius: 10, background: '#fff',
  border: `1px solid ${NEST.boutonBord}`, color: NEST.boutonTexte, fontSize: 12.5, fontWeight: 600,
  fontFamily: POLICE, cursor: 'pointer' }

export default function MonTravail({ currentUser }) {
  const [avis, setAvis] = useState([])
  const [nonRelies, setNonRelies] = useState([])
  const [controles, setControles] = useState([])
  const [noms, setNoms] = useState({})
  // Logements que le guichet refuse (hors de son périmètre aujourd'hui) :
  // nommés « un logement qui n'est plus le vôtre », jamais par l'identifiant.
  const [horsPerimetre, setHorsPerimetre] = useState(() => new Set())
  const [detail, setDetail] = useState(null)
  const [autresOuverts, setAutresOuverts] = useState(true)
  const [charge, setCharge] = useState(false)
  const [erreur, setErreur] = useState(false)
  const [photosOuvertes, setPhotosOuvertes] = useState(null)
  useEchapPourFermer(() => setPhotosOuvertes(null), !!photosOuvertes)

  useEffect(() => {
    let annule = false
    ;(async () => {
      let a = null
      let h = null
      try {
        ;[a, h] = await Promise.all([fetchMenageAvis(), fetchHistoriqueControles()])
      } catch (e) {
        console.error('[mon-travail] lecture', e?.message || 'erreur')
        if (!annule) { setErreur(true); setCharge(true) }
        return
      }
      if (annule) return
      const listeAvis = Array.isArray(a?.avis) ? a.avis : []
      const listeCtrl = Array.isArray(h?.controles) ? h.controles : []
      setAvis(listeAvis)
      setNonRelies(Array.isArray(a?.non_relies) ? a.non_relies : [])
      setControles(listeCtrl)
      setCharge(true)
      const ids = [...listeAvis, ...listeCtrl].map((x) => x.logement_id).filter(Boolean)
      if (!ids.length) return
      try {
        const infos = await fetchLogInfos(ids)
        if (annule) return
        const n = {}
        for (const [id, info] of Object.entries(infos || {})) if (info?.nom) n[id] = info.nom
        setNoms(n)
        setHorsPerimetre(new Set([...new Set(ids)].filter((id) => !(infos && infos[id]))))
      } catch (e) {
        console.error('[mon-travail] noms des logements', e?.message || 'erreur')
      }
    })()
    return () => { annule = true }
  }, [currentUser?.email])

  const nomLogement = (id) => noms[id] || (horsPerimetre.has(id) ? SANS_NOM : 'un logement')
  const moisCourant = moisCourantParis()
  const moisMin = moisLePlusAncien(moisCourant)
  const [mois, setMois] = useState(moisCourant)
  const [filtre, setFiltre] = useState('tous')

  // La tuile « ménages faits » : sa propre lecture, par mois. Elle peut rater
  // seule : les avis et les reprises restent à l'écran.
  const [moisLu, setMoisLu] = useState(null) // { mois, faits, faitsParLogement } | null
  useEffect(() => {
    let annule = false
    setMoisLu(null)
    fetchMoisAgent(mois).then((r) => { if (!annule) setMoisLu({ mois, ...r }) })
      .catch((e) => { console.error('[mon-travail] ménages du mois', e?.message || 'erreur') })
    return () => { annule = true }
  }, [mois, currentUser?.email])

  const logements = useMemo(() => [...new Set(avis.map((a) => a.logement_id).filter(Boolean))]
    .map((id) => ({ id, nom: noms[id] || (horsPerimetre.has(id) ? SANS_NOM : 'un logement') }))
    .sort((x, y) => x.nom.localeCompare(y.nom)), [avis, noms, horsPerimetre])

  const avisDuMois = useMemo(() => avis
    .filter((a) => filtre === 'tous' || a.logement_id === filtre)
    .filter((a) => moisDe(a.review_date) === mois), [avis, filtre, mois])
  // Les avis qu'on n'a pas su relier à un ménage : ils ne comptent nulle part,
  // et l'écran le dit. Un 0 mentirait : « — », jamais 0.
  const nonReliesDuMois = useMemo(() => nonRelies
    .filter((a) => filtre === 'tous' || a.logement_id === filtre)
    .filter((a) => moisDe(a.review_date) === mois).length, [nonRelies, filtre, mois])
  const proprete = useMemo(() => {
    const notes = avisDuMois.map(noteMenage).filter((v) => v != null)
    return notes.length ? notes.reduce((s, v) => s + v, 0) / notes.length : null
  }, [avisDuMois])
  const aRegarder = useMemo(() => avisDuMois
    .filter((a) => { const n = noteSur5(a); return n != null && n < 5 })
    .sort((x, y) => (noteMenage(x) ?? 9) - (noteMenage(y) ?? 9)), [avisDuMois])
  const autres = useMemo(() => avisDuMois.filter((a) => !aRegarder.includes(a)), [avisDuMois, aRegarder])
  const reprisesDuMois = useMemo(() => controles
    .filter((c) => filtre === 'tous' || c.logement_id === filtre)
    .filter((c) => moisDe(c.scheduled_date || c.repris_le) === mois), [controles, filtre, mois])
  // « Repassé derrière vous » compte TOUTES les reprises du mois (maquette :
  // R.length), qu'il les ait reprises lui-même ou non.
  const basculer = (cle) => setDetail((d) => (d === cle ? null : cle))
  const faits = moisLu ? (filtre === 'tous' ? moisLu.faits : moisLu.faitsParLogement[filtre] || 0) : null
  const menagesDuMois = useMemo(() => (moisLu?.runs || [])
    .filter((r) => filtre === 'tous' || r.logement_id === filtre), [moisLu, filtre])
  const repris = reprisesDuMois.length
  const sous5Inconnu = aRegarder.length === 0 && nonReliesDuMois > 0
  const autresTousA5 = autres.length > 0 && autres.every((a) => noteSur5(a) === 5)

  return (
    <div data-testid="mon-travail" style={{ padding: '0 0 80px', fontFamily: POLICE, color: NEST.encre }}>
      {/* ══ 1. LE BANDEAU ══ */}
      <div style={{ background: '#fff', padding: '18px 16px 16px' }}>
        <p style={{ margin: 0, fontSize: 11.5, color: NEST.gris }}>Ce qu'on dit de vos ménages, et les reprises.</p>
        <h2 style={{ margin: '2px 0 12px', fontSize: 19, fontWeight: 600, color: NEST.encre }}>Mon travail</h2>
        <div data-testid="travail-tuiles"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <Tuile cle="faits" ton={faits == null ? 'inconnu' : repris ? 'regarder' : 'bon'}
            chiffre={faits == null ? '—' : String(faits)}
            texte={faits == null ? 'ménages faits' : `ménages faits · ${Math.max(0, faits - repris)} validés du premier coup · ${repris} repris`}
            Icone={Brush} titre="Ménages faits"
            ouvert={detail === 'faits'} onOuvrir={faits == null ? null : () => basculer('faits')} />
          <Tuile cle="proprete" ton={charge && !erreur ? palierNote(proprete == null ? null : Math.round(proprete * 10) / 10) : 'inconnu'}
            chiffre={charge && !erreur ? noteFR(proprete) : '—'}
            texte={`propreté · ${charge && !erreur ? avisDuMois.length : '—'} avis${nonReliesDuMois ? ` · ${nonReliesDuMois} non relié${nonReliesDuMois > 1 ? 's' : ''}` : ''}`} Icone={Star} plein titre="Propreté"
            ouvert={detail === 'note'} onOuvrir={charge && !erreur ? () => basculer('note') : null} />
          <Tuile cle="sous5" ton={!charge || erreur || sous5Inconnu ? 'inconnu' : aRegarder.length ? 'regarder' : 'bon'}
            chiffre={!charge || erreur || sous5Inconnu ? '—' : String(aRegarder.length)}
            texte={`avis · sous 5 sur 5 en ${libelleMoisSeul(mois)}`} Icone={Star} titre="Avis sous 5 sur 5"
            ouvert={detail === 'sous5'} onOuvrir={charge && !erreur ? () => basculer('sous5') : null} />
          <Tuile cle="repasse" ton={charge && !erreur ? (reprisesDuMois.length ? 'regarder' : 'bon') : 'inconnu'}
            chiffre={charge && !erreur ? String(reprisesDuMois.length) : '—'}
            texte="fois · repassé derrière vous" Icone={RotateCcw} titre="Repassé derrière vous"
            ouvert={detail === 'rep'} onOuvrir={charge && !erreur ? () => basculer('rep') : null} />
        </div>
        {/* LE PANNEAU D'UNE TUILE — sa liste, ouverte sous le bandeau. */}
        {detail && (detail === 'faits' || (charge && !erreur)) && (
          <div data-testid={`travail-detail-${detail}`} style={{ marginTop: 10 }}>
            {detail === 'faits' && menagesDuMois.map((r) => (
              <LigneDetail key={r.id} titre={`${dateCourteFR(r.scheduled_date)} · ${noms[r.logement_id] || r.logement_nom || nomLogement(r.logement_id)}`}
                sousTitre={r.status === 'completed' || r.completed_at ? 'fait' : 'prévu'} />
            ))}
            {detail === 'note' && (<>
              {avisDuMois.map((a) => (
                <LigneDetail key={a.id} note={noteMenage(a)} titre={`${nomLogement(a.logement_id)} · ${dateCourteFR(a.review_date)}`}
                  sousTitre={a.review_text ? `« ${a.review_text} »` : null} />
              ))}
              <p style={{ fontSize: 11, color: NEST.gris, margin: '6px 0 0' }}>La note de propreté est celle que les voyageurs donnent, telle quelle.</p>
            </>)}
            {detail === 'sous5' && (aRegarder.length ? aRegarder.map((a) => (
              <LigneDetail key={a.id} note={noteSur5(a)} titre={`${nomLogement(a.logement_id)} · ${dateCourteFR(a.review_date)}`}
                sousTitre={a.review_text ? `« ${a.review_text} »` : null} />
            )) : <p style={{ fontSize: 11, color: NEST.gris, margin: 0 }}>Aucun avis sous 5 ce mois.</p>)}
            {detail === 'rep' && (reprisesDuMois.length ? reprisesDuMois.map((c) => {
              const moi = c.reprise_par !== 'controleur'
              const fait = moi && c.statut === 'regle'
              return (
                <LigneDetail key={c.id} titre={`${nomLogement(c.logement_id)} · ${dateCourteFR(c.scheduled_date)}`}
                  sousTitre={fait ? 'repris par vous-même' : moi ? 'à reprendre par vous' : 'repris sur place, pas par vous'}
                  etiquette={fait ? 'par vous' : moi ? 'à reprendre' : 'par un autre'} tonEtiquette={fait ? 'bon' : 'regarder'} />
              )
            }) : <p style={{ fontSize: 11, color: NEST.gris, margin: 0 }}>Aucune reprise sur vos ménages en {libelleMoisSeul(mois)}.</p>)}
          </div>
        )}
      </div>

      <div style={{ padding: '0 16px' }}>
        {/* ══ 2. LE MOIS ET LE LOGEMENT ══ */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, margin: '16px 0 0' }}>
        <div data-testid="travail-nav-mois" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '100 1 340px' }}>
          <button type="button" data-testid="travail-mois-prec" aria-label="Mois précédent" title="Mois précédent"
            onClick={() => setMois((m) => (m > moisMin ? moisVoisin(m, -1) : m))} disabled={mois <= moisMin}
            style={{ ...bouton, width: 44, padding: 0, fontSize: 18, borderRadius: 12,
              opacity: mois <= moisMin ? 0.4 : 1, cursor: mois <= moisMin ? 'default' : 'pointer' }}>‹</button>
          <span data-testid="travail-mois" style={{ flex: 1, textAlign: 'center', fontSize: 24, fontWeight: 700 }}>
            {libelleMoisFR(mois)}
          </span>
          <button type="button" data-testid="travail-mois-suiv" aria-label="Mois suivant" title="Mois suivant"
            onClick={() => setMois((m) => moisVoisin(m, 1))} disabled={mois >= moisCourant}
            style={{ ...bouton, width: 44, padding: 0, fontSize: 18, borderRadius: 12,
              opacity: mois >= moisCourant ? 0.4 : 1, cursor: mois >= moisCourant ? 'default' : 'pointer' }}>›</button>
        </div>
        <select data-testid="travail-filtre-logement" aria-label="Logement" value={filtre}
          onChange={(e) => setFiltre(e.target.value)}
          style={{ flex: '1 1 160px', minHeight: 44, borderRadius: 9, border: `1px solid ${NEST.bord}`,
            background: '#fff', fontSize: 12, fontFamily: POLICE, color: NEST.encre, padding: '0 8px' }}>
          <option value="tous">Tous les logements</option>
          {logements.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
        </select>
        </div>

        {!charge && <p data-testid="travail-lecture" style={{ fontSize: 12.5, color: NEST.gris, margin: '16px 2px' }}>On lit…</p>}
        {erreur && (
          <p data-testid="travail-erreur" style={{ fontSize: 12.5, color: NEST.aubTexte, margin: '16px 2px' }}>
            La lecture n'a pas abouti. Vérifiez votre connexion puis rouvrez « Mon travail ».
          </p>
        )}

        {charge && !erreur && (<>
          {/* ══ 3. LES AVIS ══ */}
          <div style={intertitre}>Les avis à regarder</div>
          {nonReliesDuMois > 0 && (
            <div data-testid="travail-non-relies" style={{ background: NEST.grisPale, borderRadius: 12, padding: '10px 13px', marginBottom: 8, fontSize: 12.5, color: NEST.grisTexte, lineHeight: 1.45 }}>
              {nonReliesDuMois} avis non relié{nonReliesDuMois > 1 ? 's' : ''} à un ménage en {libelleMoisSeul(mois)} : impossible de dire à qui {nonReliesDuMois > 1 ? 'ils reviennent' : 'il revient'}, {nonReliesDuMois > 1 ? 'ils ne comptent' : 'il ne compte'} pas dans votre note.
            </div>
          )}
          {aRegarder.length === 0 && !sous5Inconnu && (
            <div style={{ background: NEST.fondCarte, border: `1px solid ${NEST.bord}`, borderRadius: 12,
              padding: '12px 13px', fontSize: 12.5, color: NEST.gris }}>
              Aucune note basse en {libelleMoisSeul(mois)}.
            </div>
          )}
          {aRegarder.map((a) => {
            const men = noteMenage(a)
            return (
              <div key={a.id} data-testid={`travail-avis-${a.id}`}
                style={{ background: NEST.fondCarte, border: `1px solid ${NEST.bord}`, borderRadius: 12,
                  padding: '12px 13px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <b style={{ fontSize: 13, flex: 1, minWidth: 0 }}>{nomLogement(a.logement_id)} · {dateCourteFR(a.review_date)}</b>
                  <Etoiles n={noteSur5(a)} />
                  {men != null && (
                    <span data-testid={`travail-proprete-${a.id}`} data-ton={palierNote(men)}
                      style={{ borderRadius: 20, padding: '3px 8px', fontSize: 12, fontWeight: 600,
                        background: palierNote(men) === 'regarder' ? NEST.aub : palierNote(men) === 'bon' ? NEST.vert : NEST.grisPale,
                        color: palierNote(men) === 'correct' ? NEST.encre : '#fff' }}>
                      propreté {noteFR(men)}/5
                    </span>
                  )}
                </div>
                {a.review_text && <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5 }}>« {a.review_text} »</div>}
              </div>
            )
          })}

          {autres.length > 0 && (
            <details data-testid="travail-autres-avis" open={autresOuverts}
              onToggle={(e) => setAutresOuverts(e.currentTarget.open)}
              style={{ background: '#fff', border: `1px solid ${NEST.bord}`, borderRadius: 12, padding: '12px 13px' }}>
              <summary style={{ fontSize: 12.5, fontWeight: 600, color: NEST.vertFonce, cursor: 'pointer', minHeight: 24 }}>
                Les autres avis · {autres.length}{autresTousA5 ? ' · tous à 5 sur 5' : ''}
              </summary>
              {autres.map((a) => (
                <div key={a.id} data-testid={`travail-autre-avis-${a.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: NEST.vertLigne,
                    borderRadius: 8, padding: '6px 8px', marginTop: 6, fontSize: 12.5 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {nomLogement(a.logement_id)} · {dateCourteFR(a.review_date)}
                    {a.review_text && <span style={{ display: "block", fontSize: 10.5, color: NEST.grisTexte }}>« {a.review_text} »</span>}
                  </span>
                  <Etoiles n={noteSur5(a)} couleur={NEST.vert} />
                </div>
              ))}
            </details>
          )}

          {/* ══ 4. LES REPRISES ══ */}
          <div style={intertitre}>Quand on est repassé derrière vous · {libelleMoisSeul(mois)}</div>
          {reprisesDuMois.length === 0 && (
            <div style={{ background: NEST.fondCarte, border: `1px solid ${NEST.bord}`, borderRadius: 12,
              padding: '12px 13px', fontSize: 12.5, color: NEST.gris }}>
              Aucune reprise sur vos ménages en {libelleMoisSeul(mois)}.
            </div>
          )}
          {reprisesDuMois.map((c) => {
            const parAutrui = c.reprise_par === 'controleur'
            // Vert seulement quand c'est réglé : une reprise en cours reste à faire.
            const repris = !parAutrui && c.statut === 'regle'
            const photos = (c.points || []).filter(Boolean)
              .flatMap((pt) => (Array.isArray(pt?.photos) ? pt.photos : [])).filter(Boolean)
            return (
              <div key={c.id} data-testid={`travail-controle-${c.id}`} data-autrui={parAutrui ? '1' : '0'} data-repris={repris ? '1' : '0'}
                style={{ background: repris ? NEST.vertPale : NEST.aubPale,
                  border: `1px solid ${repris ? NEST.vertBord : NEST.aubBord}`,
                  borderRadius: 12, padding: '10px 13px', marginBottom: 8 }}>
                <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
                  textTransform: 'uppercase', color: repris ? NEST.vertFonce : NEST.aub }}>
                  {parAutrui ? 'Repris sur place, pas par vous' : repris ? "Vous l'avez repris vous-même" : 'À reprendre'}
                </span>
                <b style={{ display: 'block', fontSize: 14, marginTop: 3 }}>
                  {nomLogement(c.logement_id)} · {dateCourteFR(c.scheduled_date)}
                </b>
                {phraseConstat(c.points) && (
                  <span style={{ display: 'block', fontSize: 12.5, marginTop: 3, lineHeight: 1.45 }}>{phraseConstat(c.points)}</span>
                )}
                {photos.length > 0 && (
                  <button type="button" data-testid={`travail-photos-${c.id}`}
                    onClick={() => setPhotosOuvertes(photos)} style={{ ...bouton, marginTop: 9 }}>
                    {photos.length > 1 ? `Voir les ${photos.length} photos du constat` : 'Voir la photo du constat'}
                  </button>
                )}
              </div>
            )
          })}
        </>)}
      </div>

      {/* Les photos du constat, en grand — voile, clic ou Échap pour refermer. */}
      {photosOuvertes && (
        <div data-testid="travail-photo-grande" onClick={() => setPhotosOuvertes(null)}
          role="dialog" aria-modal="true" aria-label="Photo du constat"
          style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(23,33,30,.85)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            overflowY: 'auto', padding: 18, cursor: 'zoom-out' }}>
          {photosOuvertes.map((url) => (
            <img key={url} src={url} alt="Constat du contrôle"
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: 12 }} />
          ))}
        </div>
      )}
    </div>
  )
}
